import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FaExternalLinkAlt } from "react-icons/fa";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  HStack,
  Text,
} from "@chakra-ui/react";
import { FaSortAlphaDown } from "react-icons/fa";
import { FaSortAlphaDownAlt } from "react-icons/fa";
import LoadingSpinner from "../../LoadingSpinner";

const CaseSearchResultTable = ({ searchResult, handleOpenCase, isLoading }) => {
  const columns = useMemo(
    () => [
      { accessorKey: "id", header: "ID" },
      {
        header: "Name",
        accessorFn: (row) => `${row.FIRST_NAME} ${row.LAST_NAME || ""}`,
      },
      { accessorKey: "BIRTH_DATE", header: "Birth Date" },
      { accessorKey: "GENDER", header: "Gender" },
      { accessorKey: "Type", header: "Type" },
      { accessorKey: "EVENT_DATE", header: "Event Date" },
      { accessorKey: "source", header: "Source", width: "10px" },
      {
        accessorKey: "action",
        header: "Open Case",
        textAlign: "center",
        cell: ({ row }) => (
          <FaExternalLinkAlt
            cursor="pointer"
            fontSize="15px"
            onClick={() => handleOpenCase(row.original.id, row.original.source)}
          />
        ),
      },
    ],
    [handleOpenCase]
  );

  const table = useReactTable({
    data: searchResult,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Box p={1}>
      <Box minH="72vh" maxH="73vh" overflowY="scroll">
        <Table variant="simple" h="100%">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    cursor="pointer"
                  >
                    <HStack spacing="2px">
                      <Text fontSize="sm">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </Text>
                      {header.column.getIsSorted() === "asc" ? (
                        <FaSortAlphaDown />
                      ) : header.column.getIsSorted() === "desc" ? (
                        <FaSortAlphaDownAlt />
                      ) : null}
                    </HStack>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id} textAlign="center">
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      <HStack justifyContent="space-between" mt={1}>
        <Button
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          _disabled={{
            background: "gray.100",
          }}
          size="sm"
        >
          Previous
        </Button>
        <Text>
          Page <strong>{table.getState().pagination.pageIndex + 1}</strong> of{" "}
          {table.getPageCount()}
        </Text>
        <Button
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          bg="bgColor"
          size="sm"
          color="white"
          _hover={{ background: "#3c1514" }}
          _disabled={{ background: "#3c1514" }}
        >
          Next
        </Button>
      </HStack>
    </Box>
  );
};

export default CaseSearchResultTable;
